import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { assetService, userGroupService } from '../services/api';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
  FormHelperText,
  Chip,
  LinearProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  DesktopWindows as DesktopWindowsIcon,
  Headset as HeadsetIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PhotoCamera as PhotoCameraIcon,
  DeleteForever as DeleteForeverIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';

// Importar constantes e validações compartilhadas
import {
  PHYSICAL_SUBCATEGORIES,
  ASSET_STATUSES,
  SUBCATEGORY_ALIASES
} from '../constants/assetConstants';

const PhysicalAssetForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const subcategoryFromUrl = searchParams.get('subcategory');

  const [formData, setFormData] = useState({
    name: '',
    category: 'physical',
    subcategory: subcategoryFromUrl || '',
    description: '',
    owner: '',
    location: '',
    status: 'active',
    value: '',
    permission_group_ids: [],
    metadata: {},
    in_stock: true,
    with_user: false,
    invoice_link: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [groups, setGroups] = useState([]);

  const telephonyFields = [
    { name: 'phone_code', label: 'Código', type: 'text' },
    { name: 'phone_number', label: 'Número', type: 'text', required: true },
    { name: 'phone_situation', label: 'Situação', type: 'text' },
    { name: 'phone_user', label: 'Usuário', type: 'text' },
    { name: 'carrier', label: 'Operadora', type: 'select', options: PHYSICAL_SUBCATEGORIES.Telefonia?.carriers || [] },
    { name: 'phone_plan', label: 'Plano', type: 'text' },
    { name: 'phone_add_on', label: 'Add-on', type: 'text' },
    { name: 'iccid', label: 'ICCID', type: 'text' },
    { name: 'service_start_date', label: 'Data de Início do Serviço', type: 'date' },
    { name: 'fidelity_date', label: 'Fidelidade', type: 'date' },
    { name: 'line_value', label: 'Valor da linha', type: 'number', step: '0.01' },
    { name: 'mfa_code', label: 'Código do MFA', type: 'text' },
    { name: 'recovery_email', label: 'E-mail de Recuperação', type: 'email' },
    { name: 'usage_term', label: 'Termo de uso', type: 'text' },
    { name: 'usage_start_date', label: 'Data de Uso (Início)', type: 'date' },
    { name: 'usage_end_date', label: 'Data de Uso (Fim)', type: 'date' },
    { name: 'return_term', label: 'Termo de devolução', type: 'text' }
  ];

  const getSubcategoryIcon = (subcategoryKey) => {
    const icons = {
      'Computadores': <DesktopWindowsIcon />,
      'Monitores': <DesktopWindowsIcon />,
      'Impressoras': <DesktopWindowsIcon />,
      'Periféricos': <HeadsetIcon />,
      'Telefonia': <PhoneIcon />,
      'Servidores': <DesktopWindowsIcon />,
      'Consumíveis': <DesktopWindowsIcon />,
      'Outro': <DesktopWindowsIcon />
    };
    return icons[subcategoryKey] || <DesktopWindowsIcon />;
  };

  useEffect(() => {
    if (subcategoryFromUrl) {
      setFormData(prev => ({ ...prev, subcategory: subcategoryFromUrl }));
    }
  }, [subcategoryFromUrl]);

  useEffect(() => {
    if (id) {
      loadAsset(id);
    }
  }, [id]);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await userGroupService.getUserGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadAsset = async (assetId) => {
    try {
      setLoading(true);
      const response = await assetService.getAsset(assetId, 'physical');
      const asset = response.asset;

      let assetSubcategory = asset.subcategory || '';
      if (SUBCATEGORY_ALIASES[assetSubcategory]) {
        assetSubcategory = SUBCATEGORY_ALIASES[assetSubcategory];
      }

      setFormData({
        name: asset.name || '',
        category: 'physical',
        subcategory: assetSubcategory,
        description: asset.description || '',
        owner: asset.owner || '',
        location: asset.location || '',
        status: asset.status || 'active',
        value: asset.value || '',
        permission_group_ids: asset.permission_group_ids || (asset.group_id ? [asset.group_id] : []),
        metadata: asset.asset_metadata || {},
        in_stock: asset.in_stock !== undefined ? asset.in_stock : true,
        with_user: asset.with_user !== undefined ? asset.with_user : false,
        invoice_link: asset.invoice_link || '',
        computer_type: asset.computer_type || '',
        monitor_type: asset.monitor_type || '',
        printer_type: asset.printer_type || '',
        devices_type: asset.devices_type || '',
        server_type: asset.server_type || '',
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        serial_number: asset.serial_number || '',
        inventory_number: asset.inventory_number || '',
        uuid: asset.uuid || '',
        network: asset.network || '',
        size: asset.size || '',
        memory: asset.memory || '',
        cpu: asset.cpu || '',
        storage: asset.storage || '',
        brand: asset.brand || '',
        management_type: asset.management_type || '',
        current_page_counter: asset.current_page_counter || '',
        initial_page_counter: asset.initial_page_counter || '',
        snmp_credential: asset.snmp_credential || '',
        update_source: asset.update_source || '',
        phone_code: asset.phone_code || '',
        phone_number: asset.phone_number || '',
        phone_situation: asset.phone_situation || '',
        phone_user: asset.phone_user || '',
        carrier: asset.carrier || '',
        phone_plan: asset.phone_plan || '',
        phone_add_on: asset.phone_add_on || '',
        iccid: asset.iccid || '',
        service_start_date: asset.service_start_date || '',
        fidelity_date: asset.fidelity_date || '',
        line_value: asset.line_value || '',
        mfa_code: asset.mfa_code || '',
        recovery_email: asset.recovery_email || '',
        usage_term: asset.usage_term || '',
        usage_start_date: asset.usage_start_date || '',
        usage_end_date: asset.usage_end_date || '',
        return_term: asset.return_term || ''
      });

      setIsEdit(true);
    } catch (error) {
      console.error('Erro ao carregar ativo:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar ativo', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    if (name === 'name' && (!value || value.trim().length < 3)) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (name === 'name') {
      delete errors.name;
    }

    if (name === 'value' && value && isNaN(parseFloat(value))) {
      errors.value = 'Valor deve ser um número válido';
    } else if (name === 'value') {
      delete errors.value;
    }

    if (formData.subcategory) {
      const subcategoryInfo = PHYSICAL_SUBCATEGORIES[formData.subcategory];
      if (subcategoryInfo?.required_fields.includes(name) && (!value || value.trim() === '')) {
        errors[name] = `${name} é obrigatório para ${formData.subcategory}`;
      } else if (name in errors) {
        delete errors[name];
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (type !== 'checkbox') {
      validateField(name, finalValue);
    }
  };

  const getDynamicFields = () => {
    if (formData.subcategory) {
      const subcategoryInfo = PHYSICAL_SUBCATEGORIES[formData.subcategory];
      if (!subcategoryInfo) return [];

      if (formData.subcategory === 'Telefonia') {
        return telephonyFields;
      }

      const fields = [];

      subcategoryInfo.required_fields.forEach(fieldName => {
        let fieldConfig = { name: fieldName, required: true };

        if (fieldName === 'computer_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Computador',
            type: 'select',
            options: subcategoryInfo.computer_types || []
          };
        } else if (fieldName === 'monitor_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Monitor',
            type: 'select',
            options: subcategoryInfo.monitor_types || []
          };
        } else if (fieldName === 'printer_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Impressora',
            type: 'select',
            options: subcategoryInfo.printer_types || []
          };
        } else if (fieldName === 'devices_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Dispositivo',
            type: 'select',
            options: subcategoryInfo.device_types || []
          };
        } else if (fieldName === 'server_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Servidor',
            type: 'select',
            options: subcategoryInfo.server_types || []
          };
        } else if (fieldName === 'manufacturer') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Fabricante',
            type: 'select',
            options: subcategoryInfo.manufacturers || []
          };
        } else if (fieldName === 'size') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tamanho (polegadas)',
            type: 'number',
            step: '0.1'
          };
        } else if (fieldName === 'memory') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Memória (GB)',
            type: 'number'
          };
        } else {
          fieldConfig = {
            ...fieldConfig,
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' '),
            type: 'text'
          };
        }

        fields.push(fieldConfig);
      });

      const optionalFields = ['model', 'serial_number', 'inventory_number', 'network', 'uuid'];
      optionalFields.forEach(fieldName => {
        if (!subcategoryInfo.required_fields.includes(fieldName)) {
          fields.push({
            name: fieldName,
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' '),
            type: 'text',
            required: false
          });
        }
      });

      return fields;
    }

    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length < 3) {
      setValidationErrors(prev => ({ ...prev, name: 'Nome deve ter pelo menos 3 caracteres' }));
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    const allFields = getDynamicFields();
    const requiredFields = allFields.filter(f => f.required);
    let hasErrors = false;

    requiredFields.forEach(field => {
      if (!formData[field.name] || formData[field.name].trim() === '') {
        setValidationErrors(prev => ({
          ...prev,
          [field.name]: `${field.label} é obrigatório`
        }));
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        category: 'physical',
        subcategory: formData.subcategory,
        description: formData.description,
        owner: formData.owner,
        location: formData.location,
        status: formData.status,
        value: formData.value ? parseFloat(formData.value) : null,
        permission_group_ids: formData.permission_group_ids || [],
        metadata: { ...(formData.metadata || {}) }
      };

      allFields.forEach(field => {
        const value = formData[field.name];
        if (value !== undefined && value !== '') {
          if (field.type === 'number') {
            submitData[field.name] = parseFloat(value) || parseInt(value, 10);
          } else {
            submitData[field.name] = value;
          }
        }
      });

      submitData.in_stock = formData.in_stock;
      submitData.with_user = formData.with_user;
      submitData.invoice_link = formData.invoice_link || '';

      if (isEdit && id) {
        await assetService.updateAsset(id, submitData);
        setSnackbar({ open: true, message: 'Ativo atualizado com sucesso!', severity: 'success' });
      } else {
        await assetService.createAsset(submitData);
        setSnackbar({ open: true, message: 'Ativo criado com sucesso!', severity: 'success' });
      }

      setTimeout(() => navigate('/assets/category/physical'), 1500);
    } catch (error) {
      console.error('Erro no formulário:', error);
      setSnackbar({
        open: true,
        message: error.message || (isEdit ? 'Erro ao atualizar ativo' : 'Erro ao criar ativo'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const currentSubcategory = PHYSICAL_SUBCATEGORIES[formData.subcategory];
  const dynamicFields = getDynamicFields();

  if (loading && !isEdit) {
    return (
      <Layout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 3 }} />
          <Typography variant="h6">Salvando ativo...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1400px',
        mx: 'auto',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: '1200px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>Voltar</Button>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                {getSubcategoryIcon(formData.subcategory)}
              </Avatar>
              <Box>
                <Typography variant="h4">
                  {isEdit ? 'Editar Ativo' : 'Novo Ativo'} - {currentSubcategory?.label || formData.subcategory}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentSubcategory?.description || 'Ativo físico'}
                </Typography>
              </Box>
            </Box>

            {Object.keys(validationErrors).length > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={`${Object.keys(validationErrors).length} erro(s) de validação`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Card elevation={2} sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                        <InfoIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="h6">Informações Básicas</Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Nome do Ativo"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          disabled={loading}
                          error={!!validationErrors.name}
                          helperText={validationErrors.name}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth disabled>
                          <InputLabel>Categoria</InputLabel>
                          <Select value="physical" label="Categoria">
                            <MenuItem value="physical">Físicos</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth disabled>
                          <InputLabel>Subcategoria</InputLabel>
                          <Select value={formData.subcategory} label="Subcategoria">
                            <MenuItem value={formData.subcategory}>{currentSubcategory?.label || formData.subcategory}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Descrição"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          disabled={loading}
                          placeholder="Descreva detalhadamente o ativo..."
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                        <ComputerIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="h6">Campos Específicos - {currentSubcategory?.label || formData.subcategory}</Typography>
                    </Box>

                    {dynamicFields.length > 0 && (
                      <Grid container spacing={2}>
                        {dynamicFields.map((field) => (
                          <Grid item xs={12} sm={field.type === 'textarea' ? 12 : 6} md={4} key={field.name}>
                            {field.type === 'select' ? (
                              <FormControl fullWidth disabled={loading} error={!!validationErrors[field.name]}>
                                <InputLabel>{field.label} {field.required && '*'}</InputLabel>
                                <Select
                                  name={field.name}
                                  value={formData[field.name] || ''}
                                  onChange={handleChange}
                                  label={`${field.label} ${field.required ? '*' : ''}`}
                                >
                                  {(field.options || []).map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                  ))}
                                </Select>
                                {validationErrors[field.name] && (
                                  <FormHelperText>{validationErrors[field.name]}</FormHelperText>
                                )}
                              </FormControl>
                            ) : field.type === 'textarea' ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={`${field.label} ${field.required ? '*' : ''}`}
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleChange}
                                disabled={loading}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                              />
                            ) : field.type === 'number' ? (
                              <TextField
                                fullWidth
                                type="number"
                                label={`${field.label} ${field.required ? '*' : ''}`}
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleChange}
                                disabled={loading}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                                inputProps={{ step: field.step || '1' }}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                type={field.type || 'text'}
                                label={`${field.label} ${field.required ? '*' : ''}`}
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleChange}
                                disabled={loading}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                                InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                              />
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {formData.subcategory === 'Consumíveis' && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Imagens do Consumível
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<PhotoCameraIcon />}
                          disabled={loading}
                          sx={{ mb: 2 }}
                        >
                          Adicionar Imagens
                          <input
                            hidden
                            multiple
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const files = e.target.files;
                              if (!files || files.length === 0) return;

                              try {
                                const urls = Array.from(files).map(file => URL.createObjectURL(file));
                                setFormData(prev => ({
                                  ...prev,
                                  metadata: {
                                    ...(prev.metadata || {}),
                                    pictures: [...(prev.metadata?.pictures || []), ...urls]
                                  }
                                }));
                                setSnackbar({ open: true, message: `${files.length} imagem(ns) adicionada(s)!`, severity: 'success' });
                              } catch (err) {
                                setSnackbar({ open: true, message: 'Erro ao adicionar imagens', severity: 'error' });
                              }
                            }}
                          />
                        </Button>

                        {(formData.metadata?.pictures || []).length > 0 && (
                          <ImageList cols={3} rowHeight={120}>
                            {formData.metadata.pictures.map((src, idx) => (
                              <ImageListItem key={`${src}-${idx}`}>
                                <img src={src} alt={`preview-${idx}`} style={{ height: 120, objectFit: 'cover' }} />
                                <ImageListItemBar
                                  actionIcon={
                                    <IconButton
                                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                      onClick={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          metadata: {
                                            ...(prev.metadata || {}),
                                            pictures: (prev.metadata?.pictures || []).filter((_, i) => i !== idx)
                                          }
                                        }));
                                      }}
                                    >
                                      <DeleteForeverIcon />
                                    </IconButton>
                                  }
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{
                  position: 'sticky',
                  top: 24,
                  zIndex: 1
                }}>
                  <Card elevation={2} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="h6">Informações Adicionais</Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth disabled={loading}>
                            <InputLabel>Status</InputLabel>
                            <Select name="status" value={formData.status} onChange={handleChange} label="Status">
                              {Object.entries(ASSET_STATUSES).map(([key, config]) => (
                                <MenuItem key={key} value={key}>
                                  <Chip label={config.label} color={config.color} size="small" sx={{ mr: 1 }} />
                                  {config.description}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Valor (R$)"
                            name="value"
                            type="number"
                            step="0.01"
                            value={formData.value}
                            onChange={handleChange}
                            disabled={loading}
                            error={!!validationErrors.value}
                            helperText={validationErrors.value}
                            InputProps={{
                              startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Localização Física"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            disabled={loading}
                            InputProps={{
                              startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Responsável"
                            name="owner"
                            value={formData.owner}
                            onChange={handleChange}
                            disabled={loading}
                            InputProps={{
                              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl fullWidth disabled={loading}>
                            <InputLabel>Grupos de Permissão</InputLabel>
                            <Select
                              name="permission_group_ids"
                              value={formData.permission_group_ids}
                              onChange={handleChange}
                              label="Grupos de Permissão"
                              multiple
                            >
                              {groups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                  {group.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Link da Nota Fiscal"
                            name="invoice_link"
                            value={formData.invoice_link || ''}
                            onChange={handleChange}
                            disabled={loading}
                            placeholder="https://exemplo.com/nota-fiscal-123"
                            helperText="URL ou link para acessar a nota fiscal do ativo"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Controle de Ativo
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.in_stock}
                                onChange={handleChange}
                                name="in_stock"
                                disabled={loading}
                              />
                            }
                            label="Em estoque"
                          />
                          <FormHelperText>
                            Marque se o ativo está disponível em estoque
                          </FormHelperText>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.with_user}
                                onChange={handleChange}
                                name="with_user"
                                disabled={loading}
                              />
                            }
                            label="Com usuário"
                          />
                          <FormHelperText>
                            Marque se o ativo está atribuído a um usuário
                          </FormHelperText>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Ações</Typography>

                      <Box sx={{ mb: 2 }}>
                        {Object.keys(validationErrors).length === 0 ? (
                          <Alert severity="success" icon={<CheckCircleIcon />}>
                            Todos os campos obrigatórios foram preenchidos!
                          </Alert>
                        ) : (
                          <Alert severity="warning" icon={<WarningIcon />}>
                            {Object.keys(validationErrors).length} campo(s) precisam de atenção
                          </Alert>
                        )}
                      </Box>

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={<SaveIcon />}
                        disabled={loading || Object.keys(validationErrors).length > 0}
                      >
                        {loading ? 'Salvando...' : 'Salvar Ativo'}
                      </Button>

                      {Object.keys(validationErrors).length > 0 && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          Corrija os erros acima antes de salvar
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </form>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Layout>
  );
};

export default PhysicalAssetForm;
